import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, TrendingDown, Filter, Search, Lock, Crown, Clock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { API, getAuthHeaders } from '../utils/api';

export default function Signals() {
  const navigate = useNavigate();
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [directionFilter, setDirectionFilter] = useState('all');
  const [isLimited, setIsLimited] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [isTrial, setIsTrial] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState(0);

  useEffect(() => {
    const fetchSignals = async () => {
      try {
        const response = await axios.get(`${API}/signals`, { withCredentials: true, headers: getAuthHeaders() });
        // التعامل مع الاستجابة الجديدة
        if (response.data.signals !== undefined) {
          setSignals(response.data.signals);
          setFilteredSignals(response.data.signals);
          setIsLimited(response.data.is_limited || false);
          setTotalAvailable(response.data.total_available || 0);
          setIsTrial(response.data.is_trial || false);
          setTrialDaysLeft(response.data.trial_days_left || 0);
        } else {
          // للتوافق مع الاستجابة القديمة
          setSignals(response.data);
          setFilteredSignals(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch signals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSignals();
  }, []);

  useEffect(() => {
    let filtered = signals;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.market.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Filter by direction
    if (directionFilter !== 'all') {
      filtered = filtered.filter(s => s.direction === directionFilter);
    }

    setFilteredSignals(filtered);
  }, [searchTerm, statusFilter, directionFilter, signals]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-slate-400">Loading signals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="signals-page">
      {/* Page header */}
      <div>
        <h1 className="text-4xl font-bold font-['Manrope'] mb-2">Trading Signals</h1>
        <p className="text-slate-400">High-probability trade setups and market analysis</p>
      </div>

      {/* بانر التجربة المجانية */}
      {isTrial && !isLimited && (
        <Card className="bg-gradient-to-r from-purple-500/20 via-pink-500/10 to-orange-500/10 border-purple-500/30 p-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-purple-400" />
              <span className="text-purple-400 font-medium">
                🎉 تجربة مجانية: متبقي {trialDaysLeft} {trialDaysLeft === 1 ? 'يوم' : 'أيام'} | Free Trial: {trialDaysLeft} {trialDaysLeft === 1 ? 'day' : 'days'} left
              </span>
            </div>
            <Button 
              onClick={() => navigate('/pricing')}
              size="sm"
              className="bg-purple-500 hover:bg-purple-600 text-white"
            >
              Upgrade Now
            </Button>
          </div>
        </Card>
      )}

      {/* بانر الاشتراك للمستخدم المجاني */}
      {isLimited && (
        <Card className="bg-gradient-to-r from-amber-500/20 via-orange-500/10 to-red-500/10 border-amber-500/30 p-8">
          <div className="flex flex-col items-center text-center gap-6">
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center">
              <Lock className="w-10 h-10 text-amber-400" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-amber-400 mb-2">
                اشترك للوصول للإشارات | Subscribe to Access Signals
              </h3>
              <p className="text-slate-400 mb-2">
                يوجد {totalAvailable} إشارة متاحة | {totalAvailable} signals available
              </p>
              <p className="text-sm text-slate-500">
                اشترك في Pro للحصول على جميع الإشارات والتحليل الأسبوعي وإشعارات تيليجرام
              </p>
              <p className="text-sm text-slate-500">
                Subscribe to Pro to get all signals, weekly analysis, and Telegram alerts
              </p>
            </div>
            <Button 
              onClick={() => navigate('/pricing')}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold px-8 py-3 text-lg"
              data-testid="subscribe-signals-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              Subscribe Now - اشترك الآن
            </Button>
          </div>
        </Card>
      )}

      {/* Filters - فقط للمشتركين */}
      {!isLimited && (
      <Card className="bg-[#121214] border-zinc-800 p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search market..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-signals-input"
              className="pl-10 bg-zinc-950 border-zinc-800"
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="filter-status" className="bg-zinc-950 border-zinc-800">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-zinc-800">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
            </SelectContent>
          </Select>

          <Select value={directionFilter} onValueChange={setDirectionFilter}>
            <SelectTrigger data-testid="filter-direction" className="bg-zinc-950 border-zinc-800">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent className="bg-[#121214] border-zinc-800">
              <SelectItem value="all">All Directions</SelectItem>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="sell">Sell</SelectItem>
            </SelectContent>
          </Select>

          <Button 
            variant="outline" 
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('all');
              setDirectionFilter('all');
            }}
            data-testid="clear-filters-btn"
            className="border-zinc-700 hover:bg-zinc-800"
          >
            <Filter className="w-4 h-4 mr-2" />
            Clear Filters
          </Button>
        </div>
      </Card>
      )}

      {/* Signals list - فقط للمشتركين */}
      {!isLimited && (
      <div className="space-y-4">
        {filteredSignals.length === 0 ? (
          <Card className="bg-[#121214] border-zinc-800 p-12 text-center">
            <p className="text-slate-400 text-lg">No signals found</p>
          </Card>
        ) : (
          filteredSignals.map((signal) => (
            <Card 
              key={signal.signal_id} 
              className="bg-[#121214] border-zinc-800 p-6 hover:border-zinc-700 transition-all duration-200 cursor-pointer"
              onClick={() => navigate(`/signals/${signal.signal_id}`)}
              data-testid={`signal-item-${signal.signal_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-2xl font-semibold font-mono">{signal.market}</h3>
                    <Badge className={`${
                      signal.direction === 'buy' 
                        ? 'bg-emerald-400/10 text-emerald-400 ring-emerald-400/20' 
                        : 'bg-red-400/10 text-red-400 ring-red-400/20'
                    } ring-1 ring-inset`}>
                      {signal.direction.toUpperCase()}
                    </Badge>
                    <Badge className="bg-yellow-400/10 text-yellow-400 ring-1 ring-inset ring-yellow-400/20">
                      {signal.confidence} confidence
                    </Badge>
                    <Badge className={`${
                      signal.status === 'active'
                        ? 'bg-blue-400/10 text-blue-400 ring-blue-400/20'
                        : 'bg-zinc-400/10 text-zinc-400 ring-zinc-400/20'
                    } ring-1 ring-inset`}>
                      {signal.status}
                    </Badge>
                  </div>
                  
                  <p className="text-slate-400 mb-4 leading-relaxed">{signal.explanation}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Entry Zone</p>
                      <p className="font-mono text-white font-semibold">{signal.entry_zone}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Stop Loss</p>
                      <p className="font-mono text-red-400 font-semibold">{signal.stop_loss}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Take Profit</p>
                      <p className="font-mono text-emerald-400 font-semibold">{signal.take_profit}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Risk:Reward</p>
                      <p className="font-mono text-blue-400 font-semibold">{signal.risk_reward}</p>
                    </div>
                  </div>
                </div>
                
                {signal.direction === 'buy' ? (
                  <TrendingUp className="w-10 h-10 text-emerald-500 ml-6" />
                ) : (
                  <TrendingDown className="w-10 h-10 text-red-500 ml-6" />
                )}
              </div>
            </Card>
          ))
        )}
      </div>
      )}
    </div>
  );
}
